import React, { Component } from "react";
import {
	getOPEstimate,
	getOPLabel,
	getOPMunNo,
	getOPPurchase,
	getOPShare,
	getOPSize,
	isPropertySelected,
	openDetails,
	OPProps
} from "./PropertyTable";
import GridDataView, { GRID_SIZE } from "../DataView/GridDataView";
import { isEmptyStringOrNull } from "@eres/core/dist/StringUtils";
import { AssetCard } from "@10x/dubai-rest-api/dist/property_registry/entity";
import Strings from "@my-dld/assets/strings";
import { LocaleUtil } from "@eres/core";
import { ImperialSystem } from "@my-dld/utils/UnitUtils";
import { downloadCertificate, getUnitNumber, getVillaName, TabuPropertyType } from "@my-dld/utils/PropertyUtils";

interface State {
	searchKey?: string;
	page: number;
}

class PropertyCard extends Component<OPProps, State> {
	constructor( props: OPProps ) {
		super( props );
		this.state = {
			page: 1,
		};
	}

	render() {
		const { properties, type, unit, isSelectable, masterProperty, sProperties, onSelect } = this.props;
		if ( properties == null || properties.length < 1 ) {
			return null;
		}
		const { searchKey } = this.state;
		const filterData = this.getFilterData( searchKey );
		let Row = LandItem;
		switch ( type ) {
			case TabuPropertyType.VILLA:
			case TabuPropertyType.BUILDING:
				Row = VillaItem;
				break;
			case TabuPropertyType.UNIT:
				Row = UnitItem;
				break;
		}
		return <GridDataView
			title={ `${ getOPLabel( type ) } (${ properties!.length })` }
			items={ this.getPageData( filterData ) }
			onSearch={ this.onSearch }
			renderGridItem={ ( item: AssetCard ) => <Row
				key={ item.propertyId }
				property={ item }
				isSqFt={ unit.Id === ImperialSystem.Id }
				onDetails={ this.openDetails }
				onDownload={ downloadCertificate }
				isSelectable={ isSelectable }
				masterProperty={ masterProperty }
				sProperties={ sProperties }
				onSelect={ onSelect }/> }
			totalCount={ !isEmptyStringOrNull( searchKey ) ? filterData.length : properties.length }
			onPageChange={ this.onPageChange }/>;
	}

	openDetails = ( property: AssetCard ) => {
		openDetails( property, this.props.isSelectable );
	};

	private getFilterData( sKey?: string ) {
		const { properties, type, unit } = this.props;
		if ( properties && sKey ) {
			return properties.filter( ( item: AssetCard ) => {
				const filterKeys: string[] = [];
				filterKeys.push( getOPMunNo( item ) );
				filterKeys.push( LocaleUtil.getLocalValue( item.area ) );
				filterKeys.push( getOPSize( item, unit.Id === ImperialSystem.Id ) );
				filterKeys.push( LocaleUtil.getLocalValue( item.propertyStatus ) );
				filterKeys.push( getOPShare( item ) );
				filterKeys.push( getOPEstimate( item ) );
				if ( type === TabuPropertyType.VILLA || TabuPropertyType.BUILDING ) {
					filterKeys.push( getVillaName( item ) );
				}
				if ( type === TabuPropertyType.UNIT ) {
					filterKeys.push( getUnitNumber( item ) );
					filterKeys.push( LocaleUtil.getLocalValue( ( item as any ).buildingName ) );
				}
				const fItem = filterKeys.find( ( fKey: string ) => {
					return fKey && fKey.toLocaleLowerCase().includes( sKey.toLocaleLowerCase() );
				} );
				return fItem != undefined;
			} );
		}
		return properties ? properties : [];
	}

	private getPageData( filterData: AssetCard[] ) {
		const { page } = this.state;
		return filterData.slice( ( page - 1 ) * GRID_SIZE, page * GRID_SIZE );
	}

	private onSearch = ( sKey?: string ) => {
		this.setState( {
			searchKey: sKey,
			page: 1
		} );
	};

	private onPageChange = ( page: number ) => {
		this.setState( { page: page } );
	};
}

interface Props {
	property: AssetCard;
	isSqFt: boolean;
	onDownload: ( p: AssetCard ) => void;
	onDetails: ( p: AssetCard ) => void;
	isSelectable?: boolean;
	masterProperty?: AssetCard;
	sProperties?: AssetCard[];
	onSelect?: ( p: AssetCard, checked: boolean ) => void;
}

function LandItem( props: Props ) {
	const { property, isSqFt, sProperties } = props;
	const isSelected = isPropertySelected( property, sProperties );
    return <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 col-12 property-card">
		<div className={ `card ${ isSelected && "active" }` }>
            <div className="custom-card-title">
                <div>
                    <span>{getOPMunNo(property)}</span>
                    <span>{Strings.land_num_label}</span>
                </div>
                <span className="property-status-position">
                    <span>{LocaleUtil.getLocalValue(property.propertyStatus)}</span>
                    <span>{Strings.property_status.replace("<br/>", "")}</span>
                </span>
            </div>
            <div className="card-body custom-body">
				<div className="detail-holder">
                    <div className="row no-gutters property-card-detail">
						<div className="col-12">
							<span>{ LocaleUtil.getLocalValue( property.area ) }</span>
							<span>{ Strings.area_label }</span>
						</div>
					</div>
					<div className="row no-gutters property-card-detail">
						<div className="col-6">
							<span>{ getOPSize( property, isSqFt ) }</span>
							<span>{ Strings.size }</span>
						</div>
						<div className="col-6">
							<span>{ getOPShare( property ) }</span>
							<span>{ Strings.ownership }</span>
						</div>
					</div>
					<div className="row no-gutters property-card-detail col-item-3">
						<div className="col-6">
							<span className="contract-amount">{ getOPPurchase( property ) }</span>
							<span>{ `${ Strings.purchase_price } (${ Strings.aed })` }</span>
						</div>
						<div className="col-6">
							<span className="contract-amount">{ getOPEstimate( property ) }</span>
							<span>{ `${ Strings.estimated_price } (${ Strings.aed })` }</span>
						</div>
					</div>
				</div>
            </div>
            <RenderCommon {...props} />
            <PropertySelection {...props} />
		</div>
	</div>;
}

function VillaItem( props: Props ) {
	const { property, isSqFt, sProperties } = props;
	const isSelected = isPropertySelected( property, sProperties );
    return <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 col-12 property-card">
		<div className={ `card ${ isSelected && "active" }` }>
            <div className="custom-card-title">
                <div>
                    <span>{getVillaName(property)}</span>
                    <span>{Strings.name}</span>
                </div>
                <span className="property-status-position">
                    <span>{LocaleUtil.getLocalValue(property.propertyStatus)}</span>
                    <span>{Strings.property_status.replace("<br/>", "")}</span>
                </span>
            </div>
			<div className="card-body custom-body">
				<div className="detail-holder">
					<div className="row no-gutters property-card-detail">
						<div className="col-12">
							<span>{ LocaleUtil.getLocalValue( property.area ) }</span>
							<span>{ Strings.area_label }</span>
						</div>
					</div>
					<div className="row no-gutters property-card-detail col-item-3">
						<div className="col-4">
							<span>{ getOPMunNo( property ) }</span>
							<span>{ Strings.land_num_label }</span>
						</div>
						<div className="col-4">
							<span>{ getOPSize( property, isSqFt ) }</span>
							<span>{ Strings.size }</span>
						</div>
						<div className="col-4">
							<span>{ getOPShare( property ) }</span>
							<span>{ Strings.ownership }</span>
						</div>
					</div>
					<div className="row no-gutters property-card-detail col-item-3">
						<div className="col-6">
							<span className="contract-amount">{ getOPPurchase( property ) }</span>
							<span>{ `${ Strings.purchase_price } (${ Strings.aed })` }</span>
						</div>
						<div className="col-6">
							<span className="contract-amount">{ getOPEstimate( property ) }</span>
							<span>{ `${ Strings.estimated_price } (${ Strings.aed })` }</span>
						</div>
					</div>
				</div>
            </div>
            <RenderCommon {...props} />
		</div>
	</div>;
}

function UnitItem( props: Props ) {
	const { property, isSqFt, sProperties } = props;
	const isSelected = isPropertySelected( property, sProperties );
	return <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 col-12 property-card">
		<div className={ `card ${ isSelected && "active" }` }>
            <div className="custom-card-title">
                <div>
                    <span>{LocaleUtil.getLocalValue((property as any).buildingName)}</span>
                    <span>{Strings.building_name}</span>
                </div>

                <span className="property-status-position">
                    <span>{LocaleUtil.getLocalValue(property.propertyStatus)}</span>
                    <span>{Strings.property_status.replace("<br/>", "")}</span>
                </span>
            </div>
			<div className="card-body custom-body">
				<div className="detail-holder">
					<div className="row no-gutters property-card-detail">
						<div className="col-8">
							<span>{ LocaleUtil.getLocalValue( property.area ) }</span>
							<span>{ Strings.area_label }</span>
						</div>
						<div className="col-4">
							<span>{ getOPMunNo( property ) }</span>
							<span>{ Strings.land_num_label }</span>
						</div>
					</div>
					<div className="row no-gutters property-card-detail">
						<div className="col-4">
							<span>{ getUnitNumber( property ) }</span>
							<span>{ Strings.Unit_Number_Label }</span>
						</div>
						<div className="col-4">
							<span>{ getOPSize( property, isSqFt ) }</span>
							<span>{ Strings.size }</span>
						</div>
						<div className="col-4">
							<span>{ getOPShare( property ) }</span>
							<span>{ Strings.ownership }</span>
						</div>
					</div>
					<div className="row no-gutters property-card-detail col-item-3">
						<div className="col-6">
							<span className="contract-amount">{ getOPPurchase( property ) }</span>
							<span>{ `${ Strings.purchase_price } (${ Strings.aed })` }</span>
						</div>
						<div className="col-6">
							<span className="contract-amount">{ getOPEstimate( property ) }</span>
							<span>{ `${ Strings.estimated_price } (${ Strings.aed })` }</span>
						</div>
					</div>
				</div>
            </div>
            <RenderCommon {...props} />
		</div>
	</div>;
}

function RenderCommon( props: Props ) {
	const { property, onDownload, onDetails } = props;
	const canDownload = !property.blockTransaction;
	return <React.Fragment>

        <div className="property-card-action">
				{ canDownload && <button
                    className="btn btn_1_outline"
					onClick={ () => onDownload( property ) }>{ Strings.download_certificate }</button> }
				<button
                    className="btn btn_1_outline"
					onClick={ () => onDetails( property ) }>{ Strings.view_details }</button>
		</div>
	</React.Fragment>;
}

function PropertySelection( props: Props ) {
	const { property, isSelectable, masterProperty, sProperties, onSelect } = props;
	if ( isSelectable ) {
		const canChange = property.propertyId !== masterProperty?.propertyId;
		const isSelected = isPropertySelected( property, sProperties );
		const onClick = () => {
			if ( onSelect ) {
				onSelect( property, !isSelected );
			}
		};
		if ( canChange ) {
			return <div className="card-selection-btn">
                <button className="btn btn_1_outline" onClick={ onClick }>
					{ isSelected ? Strings.deselect_property : Strings.select_property }
				</button>
			</div>;
		}
		return null;
	}
	return null;
}

export default PropertyCard;
